import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Play, Flame } from 'lucide-react';
import { useLang } from '../context/LangContext';
import { templates } from '../mock/data';
import TemplateModal from './TemplateModal';

const TemplateCarousel = () => {
  const { lang } = useLang();
  const scrollRef = useRef(null);
  const [selected, setSelected] = useState(null);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.7 * (lang === 'ar' ? -1 : 1);
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  // Duplicate to create a rich carousel
  const items = [...templates, ...templates].map((t, i) => ({ ...t, key: `${t.id}-${i}` }));

  return (
    <section className="pt-12 pb-8 bg-white">
      <div className="max-w-[1400px] mx-auto">
        <div className="px-5 md:px-10 flex items-end justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center">
              <Flame className="w-5 h-5 text-white" fill="white" />
            </div>
            <div>
              <h3 className="text-[22px] md:text-[26px] font-black tracking-tight text-neutral-900 leading-none">
                {lang === 'sv' ? 'Trendar just nu' : 'رائج الآن'}
              </h3>
              <p className="text-[13px] text-neutral-500 mt-1">{lang === 'sv' ? 'Klicka för direktförhandsvisning' : 'انقر للمعاينة الفورية'}</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button onClick={() => scroll(-1)} className="w-10 h-10 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center transition-colors" aria-label="prev">
              <ChevronLeft className="w-5 h-5 rtl:rotate-180" />
            </button>
            <button onClick={() => scroll(1)} className="w-10 h-10 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center transition-colors" aria-label="next">
              <ChevronRight className="w-5 h-5 rtl:rotate-180" />
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="flex gap-3 md:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory px-5 md:px-10 pb-3" style={{ scrollbarWidth: 'none' }}>
          {items.map((tpl) => (
            <div key={tpl.key} onClick={() => setSelected(tpl)} className="group relative flex-shrink-0 w-[160px] md:w-[200px] aspect-[9/16] rounded-2xl overflow-hidden bg-neutral-900 cursor-pointer snap-start">
              <img src={tpl.img} alt={tpl.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/20" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-xl">
                  <Play className="w-5 h-5 fill-black text-black ml-0.5" />
                </div>
              </div>
              <div className="absolute top-2 left-2 rtl:left-auto rtl:right-2 bg-white/95 rounded-full px-2 py-0.5 text-[10px] font-bold text-neutral-900 uppercase">{tpl.category}</div>
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <div className="text-white font-bold text-[13px] leading-tight tracking-tight truncate">{tpl.title}</div>
                <div className="text-white/70 text-[11px] mt-0.5 font-medium">{tpl.uses}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {selected && <TemplateModal template={selected} onClose={() => setSelected(null)} />}
      <style>{`section > div > div[style*='scrollbar-width'] { -ms-overflow-style: none; } section > div > div[style*='scrollbar-width']::-webkit-scrollbar { display: none; }`}</style>
    </section>
  );
};

export default TemplateCarousel;
